/**
 * OcrModel - Pure data model for OCR operations
 * Handles recognition, parsing, and data transformation for FMC solution sheets.
 * This model contains no UI state or framework dependencies.
 */

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';
const CUBE_MOVE_PATTERN = /^(?:[URFDLB](?:w)?(?:2|')?|[xyz](?:2|')?)$/i;
const CUBE_MOVE_GLOBAL_PATTERN = /(?:[URFDLB](?:w)?(?:2|')?|[xyz](?:2|')?)/gi;

/**
 * TextNormalizer - Utility class for standardizing OCR output
 */
class TextNormalizer {
    static normalize(text) {
        if (!text) return text;
        return text
            .replace(/\|/g, '')
            .replace(/4/g, 'U')
            .replace(/\bu\b/g, 'U')
            .replace(/u/g, 'U');
    }
}

/**
 * ImageConverter - Handles image format conversions
 */
class ImageConverter {
    /**
     * Compress and convert image to base64
     * Reduces mobile photo sizes to prevent 413 errors
     */
    static async toBase64(file) {
        // Compress image before converting to base64
        const compressedFile = await this.compressImage(file);

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(compressedFile);
        });
    }

    /**
     * Compress image to reduce file size
     * Maintains quality while reducing dimensions for large mobile photos
     */
    static compressImage(file, maxWidth = 1920, maxHeight = 1920, quality = 0.85) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            img.onload = () => {
                let { width, height } = img;

                // Calculate new dimensions while maintaining aspect ratio
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width *= ratio;
                    height *= ratio;
                }

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now()
                            }));
                        } else {
                            reject(new Error('Image compression failed'));
                        }
                    },
                    'image/jpeg',
                    quality
                );
            };

            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = URL.createObjectURL(file);
        });
    }
}

/**
 * OcrApiClient - Manages communication with the OCR service
 */
class OcrApiClient {
    static async process(imageFile) {
        const base64Image = await ImageConverter.toBase64(imageFile);

        const response = await fetch(`${API_ENDPOINT}/api/ocr`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: base64Image })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `OCR service unavailable (Status: ${response.status})`);
        }

        return response.json();
    }
}

/**
 * TextExtractor - Processes raw OCR output into structured data
 */
class TextExtractor {
    static extract(ocrResult) {
        let text = ocrResult.text || '';
        text = TextNormalizer.normalize(text);

        return {
            text
        };
    }
}

/**
 * MoveFormatter - Formats and validates cube move notation
 */
class MoveFormatter {
    static format(line) {
        const cleanedLine = (line || '').replace(/\|/g, '');
        const matches = cleanedLine.match(CUBE_MOVE_GLOBAL_PATTERN);
        const moves = matches || (cleanedLine ? cleanedLine.split(/\s+/).filter(Boolean) : []);
        return moves.map(move => this.normalizeCase(move));
    }

    static normalizeCase(move) {
        // Convert all characters to uppercase except 'w'
        // This handles cases like: u -> U, d' -> D', uw2 -> Uw2, dw -> Dw
        return move.replace(/[a-z]/gi, (char) => {
            if (char.toLowerCase() === 'w' || char === 'x' || char === 'y' || char === 'z') {
                return char.toLowerCase();
            }
            return char.toUpperCase();
        });
    }

    static isValidMove(token) {
        return CUBE_MOVE_PATTERN.test(token);
    }
}

/**
 * SolutionParser - Extracts scramble and moves from OCR text
 */
class SolutionParser {
    static parse(text, skipScramble = false) {
        const cleanedText = (text || '').replace(/\|/g, '');
        const bodyText = this._extractBodyAfterScramble(cleanedText);
        const lines = bodyText.split(/\r?\n/).map(line => line.trim()).filter(Boolean);

        if (lines.length === 0) {
            return { scramble: '', moves: [] };
        }

        // If scramble is locked, skip the first line (printed scramble) and only extract moves
        const scramble = skipScramble ? '' : this._extractScramble(lines[0]);
        const moveLines = lines.slice(1); // Always skip first line (it's the printed scramble)
        const moves = this._extractMoves(moveLines);

        return { scramble, moves };
    }

    static _extractBodyAfterScramble(text) {
        const lowerText = text.toLowerCase();
        const scrambleIndex = lowerText.lastIndexOf('scramble');

        if (scrambleIndex === -1) return text;

        return text
            .slice(scrambleIndex + 'scramble'.length)
            .replace(/^[\s:]+/, '');
    }

    static _extractScramble(firstLine) {
        const tokens = MoveFormatter.format(firstLine);
        return tokens.join(' ');
    }

    static _extractMoves(moveLines) {
        const movesList = [];
        const labelWords = ['solution', 'move', 'moves', 'step', 'steps'];

        for (const line of moveLines) {
            // Skip lines that are labels (like "Solution:", "Moves:", etc.)
            const lowerLine = line.toLowerCase();
            const isLabelLine = labelWords.some(label =>
                lowerLine === label ||
                lowerLine === `${label}:` ||
                lowerLine === `${label}s` ||
                lowerLine === `${label}s:`
            );

            if (isLabelLine) {
                continue;
            }

            const tokens = MoveFormatter.format(line);

            for (const token of tokens) {
                movesList.push({
                    text: token,
                });
            }
        }

        return movesList;
    }
}

/**
 * OcrModel - Main model class for OCR operations
 * Provides the public interface for recognition and parsing
 */
class OcrModel {
    static async recognize(image) {
        const result = await OcrApiClient.process(image);
        return TextExtractor.extract(result);
    }

    static parse(text, skipScramble = false) {
        return SolutionParser.parse(text, skipScramble);
    }
}

export default OcrModel;
